import React, { Component } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import queryString from "query-string";
import { Col, Container, Row } from "react-bootstrap";
import { sanitizeUrl } from "@braintree/sanitize-url";
import SelectVerificationMethod from "./SelectVerificationMethod";
import VerifyWithDL from "./VerifyWithDL";
import http from "../../api/http-request";
import WithError from "../common/WithError";
import VerifyWithSSN from "./VerifyWithSSN";
import {
  formatDateString,
  getSessionValue,
  removeSessionValue,pageNotFound
} from "../../utils/common";
import VerificationResult from "./VerificationResult";

class IdentityVerification extends Component {
  constructor(props) {
    super(props);
    this.state = {
      verify: true,
      screen: 0,
      errorText: "",
      errorArray: [],
      scenario: "",
      spEntityID: "",
      RelayState: "",
      verifyWithDL: {
        givenName: "",
        sn: "",
        dateOfBirth: "",
        ssn: "",
        dlNumber: "",
        stateIdNumber: "",
        streetNumber: ""
      },
      verifyWithSSN: {
        givenName: "",
        sn: "",
        ssn: "",
        dateOfBirth: "",
        zipCode: "",
        streetNumber: "",
        consent: false
      }
    };
  }

  componentDidMount() {
    if (getSessionValue("idproofingParams")) {
      const search = getSessionValue("idproofingParams");
      const { spEntityID, RelayState } = queryString.parse(search);
      this.setState({
        spEntityID,
        RelayState
      });
    }
  }

  showErrors = (errorText) => {
    this.setState({
      errorText
    });
    window.scrollTo({
      behavior: "smooth",
      top: 0
    });
  };

  backToSource = () => {
    const { screen } = this.state;
    if (screen === 1) {
      this.setState({
        screen: 0
      });
    } else {
      const locationUrl = window.location.origin;
      let path;
      if (locationUrl.indexOf('dev.login.ct.gov') > 0) {
        path = '/openam/idpssoinit?metaAlias=/BOSCitizens/devidp';
      } else {
        path = '/idpssoinit?metaAlias=/boscitizens/devidp';
      }
      const redirectUrl = locationUrl + path;
      const { RelayState, spEntityID } = this.state;
      const searchString = queryString.stringify(
        { spEntityID, RelayState },
        { sort: false }
      );
      removeSessionValue("idproofingParams");
      window.location.href = sanitizeUrl(`${redirectUrl}&${decodeURIComponent(
        searchString
      )}`);
    }
  };

  retryVerification = () => {
    this.setState({
      screen: 0,
      verify: true
    });
  };

  renderScreens = () => {
    const { screen, verify, scenario } = this.state;
    const { userInfo } = this.props;
    const selectVerificationMethod = (
      <SelectVerificationMethod
        title="pages.identity.selectVerificationMethod.title"
        desc="pages.identity.selectVerificationMethod.desc"
        radioSelections={{
          label: "pages.identity.selectVerificationMethod.radioLabel",
          options: [
            {
              displayValue: "pages.identity.selectVerificationMethod.radioYes",
              value: true
            },
            {
              displayValue: "pages.identity.selectVerificationMethod.radioNo",
              value: false
            }
          ]
        }}
        onNext={(e, data) => this.onNext(e, data, 0)}
        onBack={this.backToSource}
      />
    );
    const verifyWithDLComp = (
      <VerifyWithDL
        title="pages.identity.verifyWithDL.title"
        desc="pages.identity.verifyWithDL.desc"
        userInfo={userInfo}
        onNext={(e, data) => this.onNext(e, data, 1)}
        onError={this.showErrors}
        onBack={this.backToSource}
      />
    );

    const verifyWithSSNComp = (
      <VerifyWithSSN
        title="pages.identity.verifyWithSSN.title"
        desc="pages.identity.verifyWithSSN.desc"
        onNext={(e, data) => this.onNext(e, data, 1)}
        onError={this.showErrors}
        onBack={this.backToSource}
      />
    );

    const verificationResultComp = (
      <VerificationResult
        scenario={scenario}
        onNextClick={this.backToSource}
        onRetryClick={this.retryVerification}
      />
    );

    const screens = [
      selectVerificationMethod,
      ...(verify ? [verifyWithDLComp] : [verifyWithSSNComp]),
      verificationResultComp
    ];

    return screens[screen];
  };

  onNext = (e, data, screen) => {
    this.setState(
      {
        errorText: "",
        errorArray: [],
        ...data,
        ...(screen === 0 && {
          screen: 1
        })
      },
      () => {
        if (screen === 1) {
          this.submitInfo();
        }
      }
    );
  };

  submitInfo = () => {
    const { verify, verifyWithDL } = this.state;
    if (verify) {
      const {
        dateOfBirth,
        dlNumber,
        givenName,
        sn,
        ssn,
        stateIdNumber,
        streetNumber
      } = verifyWithDL;
      const payload = {
        id: stateIdNumber === "" ? dlNumber : stateIdNumber,
        lastName: sn,
        firstName: givenName,
        dateOfBirth: formatDateString(dateOfBirth),
        ssn,
        streetNumber
      };
      const dataStoreToken = localStorage.getItem("authToken");
      http(
        {
          url: "endpoint/idProofing?_action=idProofing",
          method: "POST",
          data: {
            ...payload
          }
        },
        {
          "X-OpenIDM-DataStoreToken": dataStoreToken,
          "X-Requested-With": "XMLHttpRequest",
          "X-OpenIDM-OAuth-Login": true
        }
      ).then((res) => {
        let scenario;
        if (res.status === 200 && res.data && (res.data.code >= 200 && res.data.code < 300)) {
          const { code } = res.data;
          if (code >= 200 && code < 300) {
            scenario = "success";
          } else {
            scenario = "error";
          }
        } 
        else if(res.status === 200 && res.data && typeof res.data === 'string'){
          pageNotFound(res);
        }
        else {
          scenario = "error";
        }
        this.setState({
          screen: 2,
          scenario
        });
      });
    }
  };

  render() {
    const { t } = this.props;
    const { errorText, errorArray } = this.state;
    return (
      <>
        <div className="wrapper identity-verification">
          <div className="identity-verification__background" />
          <Container
            className={`identity-verification__card wrapper__container ${
              errorText || errorArray.length > 0 ? "haserror" : ""
            }`}
          >
            {(errorText || errorArray.length > 0) && (
              <WithError errors={errorText} errorArray={errorArray} />
            )}
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <h2 className="identity-verification__card_title">
                  {t("pages.identity.identityVerification")}
                </h2>
              </Col>
            </Row>
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <div className="wrapper__container-card">
                  {this.renderScreens()}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </>
    );
  }
}

IdentityVerification.propTypes = {
  t: PropTypes.func.isRequired,
  userInfo: PropTypes.object
};

IdentityVerification.defaultProps = {
  userInfo: {}
};

export default withTranslation()(IdentityVerification);
